import { useAuthContext } from "@/authentication/AuthProvider"
import { useViewerMaybe } from "@/authentication/ViewerProvider"
import { Button } from "@/components/ui/button"
import { FieldGroup } from "@/components/ui/field-group"
import { FormActions } from "@/components/ui/form-actions"
import { RhfField } from "@/components/ui/rhf-field"
import { useToast } from "@/components/ui/use-toast"
import { gql } from "@/generated"
import { forgotPasswordPath, rootPath } from "@/org-admins/org-admins-paths"
import { assert } from "@/util/assert"
import { displayValidationErrors } from "@/util/validations"
import { useMutation } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import { z, ZodType } from "zod"

const SignInOrgAdminMutation = gql(`
  mutation SignInOrgAdmin($input: LoginOrgAdminInput!){
    loginOrgAdmin(input: $input) {
      sessionToken
      errors {
        path
        fullMessages
      }
    }
  }
`)

type FormValues = {
  email: string
  password: string
}

const schema: ZodType<FormValues> = z.object({
  email: z
    .string()
    .min(1, "Email address is required")
    .email({ message: "Email address is invalid" }),
  password: z
    .string()
    .min(8, "A password of at least 8 characters is required"),
})

const SignInForm = ({ email }: { email?: string }) => {
  const navigate = useNavigate()
  const { reloadViewer } = useViewerMaybe()
  const { setToken } = useAuthContext()
  const [execCreateUser] = useMutation(SignInOrgAdminMutation)
  const { toast } = useToast()

  const defaultValues: FormValues = {
    email: email || "",
    password: "",
  }

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })
  const { control, formState, setError } = methods

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    try {
      const { data } = await execCreateUser({
        variables: {
          input: {
            email: values.email,
            password: values.password,
          },
        },
      })

      if (data?.loginOrgAdmin.errors && data?.loginOrgAdmin.errors.length > 0) {
        throw data?.loginOrgAdmin.errors
      }

      assert(data?.loginOrgAdmin?.sessionToken)
      setToken(data.loginOrgAdmin.sessionToken)
      await reloadViewer()
      navigate(rootPath.pattern)
    } catch (e: any) {
      console.error(e)
      displayValidationErrors({
        recordType: "user",
        errors: e,
        setError,
        toast,
        validationErrorTitle: "Sign In Failed",
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <FieldGroup>
          <RhfField
            name="email"
            control={control}
            placeholder="Email address"
            label="Email Address"
            autoFocus={!email}
            disabled={!!email}
          />
        </FieldGroup>
        <FieldGroup>
          <RhfField
            name="password"
            control={control}
            type="password"
            placeholder="Password"
            label="Password"
            autoFocus={!!email}
            rightLabelContent={
              <Link
                to={forgotPasswordPath.pattern}
                className="text-primary text-sm"
              >
                Forgot Password?
              </Link>
            }
          />
        </FieldGroup>
        <FormActions>
          <Button
            type="submit"
            variant="default"
            disabled={formState.isSubmitting}
          >
            Sign In
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  )
}

export default SignInForm
